import {
  PlanningAllTabFormValues,
  PlanningTabFormValues,
  TPlanningFilters,
  TPlanningTab,
  TSearchPlanningTab,
} from '../planning.type'
import { FormProvider, useForm } from 'react-hook-form'
import { PlanningTabFilterFields } from './planning-tab-filters-field.component'
import { PlanningTabPositionField } from './planning-tab-position-field.component'
import { PlanningTabColumnsField } from './planning-tab-columns-field.component'
import { CurecallButton, ECurecallButtonAppearance } from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import PlanningTabLabelField from './planning-tab-label-field.component'
import { DEFAULT_COLUMNS } from '../planning-all-tab.util'
import { PlanningService } from 'src/services/planning.service'
import { useCurrentProject } from 'src/providers/current-project.provider'
import PlanningTabRangeOfDaysFields from './planning-tab-range-of-days-fileds.component'
import { useSubmitPlanningTab } from './use-submit-planning-tab.hook'

function PlanningTabFields() {
  return (
    <div className="c-flex c-flex-col c-gap-6">
      <div className="c-flex c-gap-6">
        <PlanningTabLabelField />
        <PlanningTabRangeOfDaysFields />
      </div>
      <PlanningTabFilterFields />
      <div className="c-flex c-gap-10">
        <PlanningTabColumnsField />
        <PlanningTabPositionField />
      </div>
    </div>
  )
}

const FormActions = ({ onClose }: { onClose: VoidFunction }) => {
  const { t } = useTranslation()

  return (
    <div className="c-flex c-justify-center c-gap-4 c-mt-8">
      <CurecallButton appearance={ECurecallButtonAppearance.CANCEL} type="button" onClick={onClose}>
        {t('pages.planning.form.cancel')}
      </CurecallButton>
      <CurecallButton appearance={ECurecallButtonAppearance.PRIMARY} type="submit">
        {t('pages.planning.form.validate')}
      </CurecallButton>
    </div>
  )
}

function AddPlanningTabForm({
  valuesToCopy,
  currentFilters,
  moveToTab,
  onClose,
}: {
  valuesToCopy?: Partial<TPlanningTab>
  currentFilters: TPlanningFilters
  moveToTab: (tab: TPlanningTab) => void
  onClose: VoidFunction
}) {
  const { t } = useTranslation()
  const { project, planningTabs } = useCurrentProject()

  const methods = useForm<PlanningTabFormValues>({
    defaultValues: {
      label: '',
      relativeRangeOfDays: valuesToCopy?.relativeRangeOfDays || [-3, 3],
      followupIdsFilter: valuesToCopy?.followupIdsFilter || currentFilters?.followupIds || [],
      medEventIdsFilter: valuesToCopy?.medEventIdsFilter || currentFilters?.medEventIds || [],
      medicalStaffMemberIdsFilter:
        valuesToCopy?.medicalStaffMemberIdsFilter || currentFilters?.medicalStaffMemberIds || [],
      locationIdsFilter: valuesToCopy?.locationIdsFilter || currentFilters?.locationIds || [],
      tabPosition: planningTabs.length + 1,
      columns: valuesToCopy?.columns || DEFAULT_COLUMNS,
    },
  })

  const onSubmit = useSubmitPlanningTab(
    (values: PlanningTabFormValues) => PlanningService.postNewTab(project.id, values),
    {
      successTitle: t('pages.planning.form.notify-success-title'),
      successMessage: t('pages.planning.form.notify-success'),
      errorTitle: t('pages.planning.form.notify-error'),
    },
    (newTab) => {
      moveToTab(newTab)
      onClose()
    },
  )

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <PlanningTabFields />
        <FormActions onClose={onClose} />
      </form>
    </FormProvider>
  )
}

function UpdatePlanningTabForm({
  planningTabToUpdate,
  moveToTab,
  onClose,
}: Readonly<{
  planningTabToUpdate: TPlanningTab
  moveToTab: (tab: TPlanningTab) => void
  onClose: VoidFunction
}>) {
  const { t } = useTranslation()
  const { project } = useCurrentProject()

  const methods = useForm<PlanningTabFormValues>({
    defaultValues: {
      id: planningTabToUpdate.id,
      label: planningTabToUpdate.label,
      relativeRangeOfDays: planningTabToUpdate.relativeRangeOfDays,
      followupIdsFilter: planningTabToUpdate.followupIdsFilter || [],
      medEventIdsFilter: planningTabToUpdate.medEventIdsFilter || [],
      medicalStaffMemberIdsFilter: planningTabToUpdate.medicalStaffMemberIdsFilter || [],
      locationIdsFilter: planningTabToUpdate.locationIdsFilter || [],
      tabPosition: planningTabToUpdate.tabPosition,
      columns: planningTabToUpdate.columns,
    },
  })

  const onSubmit = useSubmitPlanningTab(
    (values: PlanningTabFormValues) => {
      delete values.id // id is not expected in the payload
      return PlanningService.putPlanningTab(project.id, planningTabToUpdate.id, values)
    },
    {
      successTitle: t('pages.planning.form.notify-update-success-title'),
      successMessage: t('pages.planning.form.notify-update-success'),
      errorTitle: t('pages.planning.form.notify-error-title'),
    },
    (updatedTab) => {
      moveToTab(updatedTab)
      onClose()
    },
  )

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <PlanningTabFields />
        <FormActions onClose={onClose} />
      </form>
    </FormProvider>
  )
}

function UpdateAllPlanningTabForm({
  allPlanningTab,
  onClose,
}: Readonly<{
  allPlanningTab: TSearchPlanningTab
  onClose: VoidFunction
}>) {
  const { t } = useTranslation()
  const { project } = useCurrentProject()

  const methods = useForm<PlanningAllTabFormValues>({
    defaultValues: { columns: allPlanningTab.columns },
  })

  const onSubmit = useSubmitPlanningTab(
    (values: PlanningAllTabFormValues) => PlanningService.putAllPlanningTab(project.id, values),
    {
      successTitle: t('pages.planning.form.notify-update-success-title'),
      successMessage: t('pages.planning.form.notify-update-success'),
      errorTitle: t('pages.planning.form.notify-error-title'),
    },
    onClose,
  )

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <PlanningTabColumnsField />
        <FormActions onClose={onClose} />
      </form>
    </FormProvider>
  )
}

export { AddPlanningTabForm, UpdatePlanningTabForm, UpdateAllPlanningTabForm }
